import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import GuidePage from "@/components/marketing/GuidePage";
import BestBottle from "@/components/shop/BestBottle";
import { PSEO_BY_SLUG } from "@/content/pseo";
import NotFound from "@/pages/NotFound";

const EYEBROW = { dish: "Cooking with coconut oil", compare: "Compare oils", use: "Everyday uses", learn: "Coconut oil science" };

/** Renders "[text](/path)" and "[text](https://...)" inside plain strings. */
export function RichText({ text }) {
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const [, label, href] = m;
    parts.push(
      href.startsWith("/") ? (
        <Link key={m.index} to={href}>{label}</Link>
      ) : (
        <a key={m.index} href={href} target="_blank" rel="noopener noreferrer">{label}</a>
      )
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}

function SectionBody({ s }) {
  return (
    <>
      {(s.paragraphs || []).map((p, i) => <p key={i}><RichText text={p} /></p>)}
      {s.list?.length > 0 && (
        <ul>{s.list.map((li, i) => <li key={i}><RichText text={li} /></li>)}</ul>
      )}
      {s.steps?.length > 0 && (
        <ol>{s.steps.map((li, i) => <li key={i}><RichText text={li} /></li>)}</ol>
      )}
      {s.table && (
        <div className="overflow-x-auto">
          <table>
            {s.table.caption && <caption>{s.table.caption}</caption>}
            <thead><tr>{s.table.head.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
            <tbody>
              {s.table.rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => (j === 0 ? <th key={j} scope="row"><RichText text={String(c)} /></th> : <td key={j}><RichText text={String(c)} /></td>))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * One template for every programmatic page (dish, oil comparison, everyday
 * use). Content lives in src/content/pseo/*.js as plain data.
 */
export default function PseoPage() {
  const { pathname } = useLocation();
  const page = PSEO_BY_SLUG[pathname.replace(/^\/|\/$/g, "")];
  if (!page) return <NotFound />;

  const sections = page.sections.map((s, i) => ({
    id: s.id || `s${i}`,
    heading: s.heading,
    body: (
      <>
        <SectionBody s={s} />
        {i === 0 && page.bestFor && <BestBottle bestFor={page.bestFor} why={page.bestForWhy} keyword={page.keyword} />}
      </>
    ),
  }));

  return (
    <GuidePage
      path={`/${page.slug}`}
      seoTitle={page.seoTitle}
      seoDescription={page.seoDescription}
      eyebrow={EYEBROW[page.template] || "Learn"}
      title={page.title}
      tldr={page.tldr}
      updated={page.updated}
      sections={sections}
      faqs={page.faqs}
      sources={page.sources}
      related={page.related}
      testid={`pseo-${page.slug}`}
    />
  );
}
